import { useMemo } from 'react'
import { Smile, Sparkles, Heart, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import {
  fetchEmojiSets,
  fetchEmojiStatuses,
  fetchReactions,
  fetchEmojiModeration,
  CAT_STR as S,
  type EmojiSetRow,
  type EmojiStatusRow,
  type ReactionRow,
  type EmojiModerationRow,
} from './catalogApi'

const modStatusOptions = [
  { value: 'pending', label: S.pending, status: 'pending' as const },
  { value: 'active', label: S.active, status: 'active' as const },
  { value: 'rejected', label: S.rejected, status: 'rejected' as const },
]

/** A21.07 — Custom emoji to'plamlari. Greenfield → fail-soft bo'sh. */
export function CustomEmojiSetsScreen() {
  const columns = useMemo<AdminColumn<EmojiSetRow>[]>(
    () => [
      {
        key: 'title',
        header: S.title,
        type: 'text',
        minWidth: 220,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.title || '—'}</span>
            <span className="block truncate text-xs text-secondary">
              {r.short_name ? `/${r.short_name}` : ''}
              {r.owner_name ? ` · ${r.owner_name}` : ''}
            </span>
          </div>
        ),
      },
      { key: 'emoji_count', header: S.emojiCount, type: 'number', width: 110, align: 'right' },
      { key: 'status', header: S.status, type: 'enum', width: 150, filterable: true, options: modStatusOptions },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.emojiSets} screenId="A21.07" />
      <DataTable<EmojiSetRow>
        screenId="A21.07"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchEmojiSets}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Smile size={22} /> }}
      />
    </>
  )
}

/** A21.08 — Emoji statuslar (profil status-belgilari). Greenfield → fail-soft bo'sh. */
export function EmojiStatusesScreen() {
  const columns = useMemo<AdminColumn<EmojiStatusRow>[]>(
    () => [
      { key: 'emoji', header: S.emoji, type: 'text', width: 90 },
      { key: 'title', header: S.title, type: 'text', minWidth: 200 },
      { key: 'is_premium', header: S.premium, type: 'bool', width: 120 },
      { key: 'uses', header: S.uses, type: 'number', width: 120, align: 'right' },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 130,
        filterable: true,
        options: [
          { value: 'active', label: S.active, status: 'active' },
          { value: 'hidden', label: S.rejected, status: 'deleted' },
        ],
      },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.emojiStatuses} screenId="A21.08" />
      <DataTable<EmojiStatusRow>
        screenId="A21.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchEmojiStatuses}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Sparkles size={22} /> }}
      />
    </>
  )
}

/**
 * A21.09 — Reaksiyalar boshqaruvi. GET /admin/analytics/reactions → eng ko'p
 * ishlatilgan reaksiya-emojilar (message_reactions bo'yicha) va ularning sanog'i.
 */
export function ReactionsManagementScreen() {
  const columns = useMemo<AdminColumn<ReactionRow>[]>(
    () => [
      {
        key: 'emoji',
        header: S.emoji,
        type: 'text',
        width: 120,
        render: (r) => <span className="text-2xl leading-none">{r.emoji}</span>,
      },
      { key: 'count', header: S.usageCount, type: 'number', minWidth: 160, align: 'right' },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.reactions} screenId="A21.09" />
      <DataTable<ReactionRow>
        screenId="A21.09"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchReactions}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Heart size={22} /> }}
      />
    </>
  )
}

/** A21.10 — Emoji moderatsiyasi. Greenfield → fail-soft bo'sh. */
export function EmojiModerationScreen() {
  const columns = useMemo<AdminColumn<EmojiModerationRow>[]>(
    () => [
      { key: 'title', header: S.title, type: 'text', minWidth: 200 },
      { key: 'owner_name', header: S.owner, type: 'text', width: 160 },
      { key: 'emoji_count', header: S.emojiCount, type: 'number', width: 110, align: 'right' },
      { key: 'status', header: S.status, type: 'enum', width: 150, filterable: true, options: modStatusOptions },
      { key: 'reason', header: S.reason, type: 'text', minWidth: 180 },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.emojiModeration} screenId="A21.10" />
      <DataTable<EmojiModerationRow>
        screenId="A21.10"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchEmojiModeration}
        rowId={(r) => String(r.id)}
        emptyState={{ icon: <ShieldCheck size={22} />, title: S.emptyQueue }}
      />
    </>
  )
}
